import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonNote,
    IonRow,
    IonText,
    IonTextarea,
    IonTitle,
    IonToolbar,
    ModalController,
} from '@ionic/angular/standalone';
import { Parser } from 'expr-eval';
import { CalculatedSignalProvider } from '../../providers/calculated-signal-provider';
import { DataProvider } from '../../providers/data-provider';

@Component({
    selector: 'app-edit-calculated-signal',
    templateUrl: 'edit-calculated-signal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        IonHeader,
        IonToolbar,
        IonButtons,
        IonButton,
        IonTitle,
        IonContent,
        IonGrid,
        IonRow,
        IonCol,
        IonItem,
        IonLabel,
        IonInput,
        IonTextarea,
        IonIcon,
        IonText,
        IonModal,
        IonList,
        IonNote,
    ],
    styles: [
        `
            .error-box {
                border: 1px solid var(--ion-color-danger);
                border-radius: 4px;
                padding: 4px;
            }
            .success-box {
                border: 1px solid var(--ion-color-success);
                border-radius: 4px;
                padding: 4px;
            }
            .keypad-btn {
                margin: 2px;
                font-weight: bold;
                width: 100%;
                height: 100%;
            }
            .popover-btn-label {
                font-size: 0.9em;
                white-space: normal !important;
            }
        `,
    ],
})
export class EditCalculatedSignalComponent implements OnInit {
    readonly editId = input<string>();

    name = '';
    unit = '';
    formula = '';
    precision = 1;

    parserError = signal<string | null>(null);
    duplicateNameError = signal<string | null>(null);
    previewResult = signal<string | null>(null);
    generatedKey = '';

    availableSignals: { key: string; name: string }[] = [];
    standardFunctions = [
        { insert: 'sma(,)', name: 'Simple Moving Average', desc: 'sma(value, window)' },
        { insert: 'lpf(,)', name: 'Low Pass Filter', desc: 'lpf(value, alpha)' },
        { insert: 'roc()', name: 'Rate of Change', desc: 'roc(value)' },
        { insert: 'integral()', name: 'Integral (Area)', desc: 'integral(value)' },
        { insert: 'min(,)', name: 'Minimum', desc: 'min(a, b)' },
        { insert: 'max(,)', name: 'Maximum', desc: 'max(a, b)' },
        { insert: 'abs()', name: 'Absolute Value', desc: 'abs(x)' },
        { insert: 'round()', name: 'Round', desc: 'round(x)' },
        { insert: 'floor()', name: 'Floor', desc: 'floor(x)' },
        { insert: 'ceil()', name: 'Ceiling', desc: 'ceil(x)' },
        { insert: 'sqrt()', name: 'Square Root', desc: 'sqrt(x)' },
        { insert: 'PI', name: 'Pi Constant', desc: '3.14159...' },
        { insert: 'sin()', name: 'Sine', desc: 'sin(x)' },
        { insert: 'cos()', name: 'Cosine', desc: 'cos(x)' },
    ];

    private parser = new Parser();
    private readonly modalCtrl = inject(ModalController);
    private readonly calcProvider = inject(CalculatedSignalProvider);
    private readonly dataProvider = inject(DataProvider);

    constructor() {
        // Seed dummy functions so the live syntax preview validator passes successfully
        this.parser.functions.sma = (val: number, _window: number) => val;
        this.parser.functions.lpf = (val: number, _alpha: number) => val;
        this.parser.functions.roc = (val: number) => val;
        this.parser.functions.integral = (val: number) => val;
    }

    ngOnInit() {
        if (this.editId()) {
            const existing = this.calcProvider.signals().find((s) => s.id === this.editId());
            if (existing) {
                this.name = existing.name;
                this.unit = existing.unit;
                this.formula = existing.formula;
                this.precision = existing.precision;
            }
        }

        // Load available signals from the current data set
        const data = this.dataProvider.data();
        if (data?.signals) {
            this.availableSignals = Object.values(data.signals).map((s) => ({
                key: s.key,
                name: s.name,
            }));
            // Sort alphabetically for easier finding
            this.availableSignals.sort((a, b) => a.name.localeCompare(b.name));
        }

        this.validateName();
        this.validateFormula();
    }

    onNameChange() {
        this.validateName();
    }

    validateName() {
        const trimmed = this.name.trim();
        if (!trimmed) {
            this.duplicateNameError.set(null);
            this.generatedKey = '';
            return;
        }

        this.generatedKey = this.toCamelCase(trimmed);

        const conflictCalc = this.calcProvider
            .signals()
            .find((s) => s.id !== this.editId() && this.toCamelCase(s.name) === this.generatedKey);

        // Filter out known calculated signals so we strictly check against RAW hardware telemetry
        const calculatedKeys = new Set(this.calcProvider.signals().map((s) => this.toCamelCase(s.name)));
        const conflictBase = this.availableSignals.find((s) => s.key === this.generatedKey && !calculatedKeys.has(s.key));

        if (conflictCalc) {
            this.duplicateNameError.set(
                `A calculated signal named "${conflictCalc.name}" already uses the variable "${this.generatedKey}".`,
            );
        } else if (conflictBase) {
            this.duplicateNameError.set(
                `The generated variable "${this.generatedKey}" conflicts with an existing primary telemetry signal.`,
            );
        } else {
            this.duplicateNameError.set(null);
        }
    }

    toCamelCase(str: string): string {
        let s = str.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
        if (!s) return 'signal';
        let words = s.split(/\s+/);
        let result =
            words[0].toLowerCase() +
            words
                .slice(1)
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join('');
        if (/^[0-9]/.test(result)) result = '_' + result;
        return result;
    }

    onFormulaChange() {
        this.validateFormula();
    }

    insertText(text: string, modalIdToClose?: string) {
        // Simple append for now. In a real scenario we'd track textarea cursor position.
        this.formula += text;
        this.validateFormula();

        // Close the specific modal if requested
        if (modalIdToClose) {
            this.modalCtrl.dismiss(null, 'cancel', modalIdToClose);
        }
    }

    validateFormula() {
        if (!this.formula.trim()) {
            this.parserError.set(null);
            this.previewResult.set(null);
            return;
        }

        try {
            const expr = this.parser.parse(this.formula);
            this.parserError.set(null);

            // Try to generate a preview if data is available
            const data = this.dataProvider.data();
            if (data?.signals) {
                const context: Record<string, number> = { time: 0 };
                // Grab the very first data point from all required variables
                const vars = expr.variables();
                let missingVars = false;

                for (const v of vars) {
                    if (v === 'time') continue; // Always provided
                    const sigData = data.signals[v]?.data;
                    if (sigData && sigData.length > 0) {
                        context[v] = sigData[0][1]; // first row, value column
                    } else {
                        missingVars = true;
                    }
                }

                if (!missingVars && vars.length > 0) {
                    const res = expr.evaluate(context);
                    this.previewResult.set(res.toFixed(this.precision));
                } else if (vars.length === 0) {
                    // pure math formula like '2 * 5'
                    this.previewResult.set(expr.evaluate(context).toFixed(this.precision));
                } else {
                    this.previewResult.set('Valid syntax, but variables missing from current log.');
                }
            } else {
                this.previewResult.set('Valid syntax. Load a log file to preview.');
            }
        } catch (err: any) {
            let msg = err.message || 'Invalid Formula';
            if (msg.includes('TEOF: EOF')) {
                msg = 'Incomplete formula (missing a variable, number, or closing parenthesis at the end).';
            } else if (msg.includes("closing ')'")) {
                msg = 'Missing a closing parenthesis ")".';
            } else if (msg.includes('unexpected T(')) {
                msg = 'Unexpected parenthesis. Did you forget an operator like +, -, *, /?';
            } else if (msg.includes('unexpected TNUMBER')) {
                msg = 'Unexpected number placed after another value. Missing an operator?';
            } else if (msg.includes('unexpected TIDENTIFIER')) {
                msg = 'Unexpected variable name placed directly after another value. Missing an operator?';
            } else if (msg.startsWith('unexpected')) {
                msg = 'Incomplete or incorrectly formatted math operation.';
            } else if (msg.includes('arguments expected')) {
                msg = msg.trim() + ' inside the function parenthesis.';
            }

            this.parserError.set(msg);
            this.previewResult.set(null);
        }
    }

    save() {
        if (this.parserError() || this.duplicateNameError()) return; // Disallow saving invalid formulas or duplicate names

        const payload = {
            name: this.name || 'Untitled Signal',
            unit: this.unit || '',
            precision: this.precision,
            formula: this.formula,
        };

        const editId = this.editId();
        if (editId) {
            this.calcProvider.updateSignal(editId, payload);
        } else {
            this.calcProvider.addSignal(payload);
        }
        this.modalCtrl.dismiss(true);
    }

    cancel() {
        this.modalCtrl.dismiss();
    }
}
